import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold font-display transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-teal text-primary-foreground shadow-md hover:bg-teal/90 hover:shadow-teal-glow",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-glass-border bg-glass-surface backdrop-blur-sm text-foreground shadow-sm hover:bg-glass-hover hover:text-teal",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: 
          "text-muted-foreground hover:bg-glass-hover hover:text-foreground",
        link: 
          "text-teal underline-offset-4 hover:underline",
        // Tattva Premium Variants
        premium:
          "bg-gradient-to-r from-teal to-teal-light text-primary-foreground shadow-lg hover:shadow-teal-glow hover:scale-105",
        accent:
          "bg-gradient-to-r from-orange to-orange-light text-accent-foreground shadow-lg hover:shadow-orange-glow hover:scale-105",
        glass:
          "bg-glass-surface backdrop-blur-xl border border-glass-border text-foreground hover:bg-glass-hover hover:border-teal/30",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
